import { useState, useEffect } from 'react';
import Input from '../../ui/Input';
import Button from '../../ui/Button';
import { useAuth } from '../../../hooks/useAuth';
import { useFirestore } from '../../../hooks/useFirestore';

interface WithdrawalRequestProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface Institution {
  id: string;
  institutionId: string;
  name: string;
  currency?: {
    code: string;
    symbol: string;
    name: string;
  };
}

const WithdrawalRequest: React.FC<WithdrawalRequestProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { user } = useAuth();
  const { addDocument } = useFirestore('withdrawals');
  // Récupérer les institutions pour avoir la devise
  const { data: institutionsData, getDocuments: getInstitutions } = useFirestore('institutions');
  
  const [isLoading, setIsLoading] = useState(false);
  const [loadingInstitution, setLoadingInstitution] = useState(true);
  const [error, setError] = useState('');
  const [institutionCurrency, setInstitutionCurrency] = useState({
    code: 'XOF',
    symbol: 'CFA',
    name: 'Franc CFA'
  });

  const [formData, setFormData] = useState({
    amount: '',
    reason: '',
    recipientName: '',
    recipientAccount: '',
    recipientBank: '',
  });

  // Charger les institutions au montage
  useEffect(() => {
    const loadInstitutionData = async () => {
      if (user?.institutionId) {
        try {
          setLoadingInstitution(true);
          await getInstitutions();
        } catch (err) {
          console.error('Erreur lors du chargement des institutions:', err);
        } finally {
          setLoadingInstitution(false);
        }
      } else {
        setLoadingInstitution(false);
      }
    };

    loadInstitutionData();
  }, [user?.institutionId, getInstitutions]);

  // Récupérer la devise de l'institution
  useEffect(() => {
    if (user?.institutionId && institutionsData && institutionsData.length > 0) {
      const institutions: Institution[] = (institutionsData as any) || [];
      
      // Trouver l'institution de l'utilisateur par institutionId
      const userInstitution = institutions.find(inst => 
        inst.institutionId === user.institutionId
      );
      
      if (userInstitution) {
        if (userInstitution.currency) {
          setInstitutionCurrency({
            code: userInstitution.currency.code || 'XOF',
            symbol: userInstitution.currency.symbol || 'CFA',
            name: userInstitution.currency.name || 'Franc CFA'
          });
        } else {
          setInstitutionCurrency({
            code: 'XOF',
            symbol: 'CFA',
            name: 'Franc CFA'
          });
        }
      } else {
        // Institution non trouvée
        setInstitutionCurrency({
          code: 'XOF',
          symbol: 'CFA',
          name: 'Franc CFA'
        });
      }
    }
  }, [user?.institutionId, institutionsData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.amount || !formData.reason || !formData.recipientName) {
      setError('Veuillez remplir tous les champs obligatoires');
      return false;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Montant invalide');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await addDocument({
        ...formData,
        amount: parseFloat(formData.amount),
        institutionId: user?.institutionId,
        institutionCurrency: institutionCurrency, // Ajouter la devise
        requestedBy: user?.uid,
        requestedByName: user?.displayName || user?.email,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      onSuccess();
    } catch (err) {
      console.error('Error creating withdrawal request:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitting = isLoading || loadingInstitution;

  // Afficher un indicateur de chargement pendant le chargement de la devise
  if (loadingInstitution) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="text-gray-600">Chargement de la devise de l'institution...</p>
        <p className="text-sm text-gray-500">ID institution: {user?.institutionId || 'Non défini'}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Information sur la devise */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-blue-900">
              Devise de l'institution
            </p>
            <p className="text-xs text-blue-700">
              {institutionCurrency.name} ({institutionCurrency.code})
            </p>
          </div>
          <div className="text-lg font-bold text-blue-600">
            {institutionCurrency.symbol}
          </div>
        </div>
      </div>

      <div>
        <Input
          label={`Montant à retirer (${institutionCurrency.symbol}) *`}
          type="number"
          name="amount"
          value={formData.amount}
          onChange={handleChange}
          required
          disabled={isSubmitting}
          placeholder="0.00"
          step="0.01"
          min="0"
        />
        <p className="mt-1 text-xs text-gray-500">
          Devise: {institutionCurrency.name} ({institutionCurrency.code})
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Raison du retrait *
        </label>
        <textarea
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          required
          disabled={isSubmitting}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Décrivez la raison de ce retrait..."
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Input
            label="Nom du bénéficiaire *"
            name="recipientName"
            value={formData.recipientName}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            placeholder="Nom complet"
          />
        </div>
        <div>
          <Input
            label="Numéro de compte"
            name="recipientAccount"
            value={formData.recipientAccount}
            onChange={handleChange}
            disabled={isSubmitting}
            placeholder="Numéro de compte bancaire"
          />
        </div>
      </div>

      <div>
        <Input
          label="Banque"
          name="recipientBank"
          value={formData.recipientBank}
          onChange={handleChange}
          disabled={isSubmitting}
          placeholder="Nom de la banque"
        />
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Information importante</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ul className="list-disc pl-5 space-y-1">
                <li>Cette demande sera soumise pour approbation au superviseur.</li>
                <li>Vous serez notifié une fois la demande traitée.</li>
                <li>La demande sera enregistrée en {institutionCurrency.name} ({institutionCurrency.symbol}).</li>
                <li>Assurez-vous que les informations bancaires sont correctes.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
        >
          {isSubmitting ? 'Soumission...' : 'Soumettre la demande'}
        </Button>
      </div>
    </form>
  );
};

export default WithdrawalRequest;